import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

/*
 * Token Storage Utility
 * ─────────────────────
 * Centralizes all token read/write/clear operations.
 * Uses localStorage intentionally — iOS Safari's Intelligent Tracking Prevention
 * blocks cross-site httpOnly cookies in iframes and PWAs. This tradeoff is
 * documented and accepted. XSS risk is mitigated by strict CSP headers.
 */
const TokenStore = {
  getAccess:    () => localStorage.getItem('access_token'),
  getRefresh:   () => localStorage.getItem('refresh_token'),
  setTokens:    (access, refresh) => { localStorage.setItem('access_token', access); if (refresh) localStorage.setItem('refresh_token', refresh); },
  clear:        () => { localStorage.removeItem('access_token'); localStorage.removeItem('refresh_token'); },
};

// Default request timeout — prevents endless loading screens if any single
// endpoint hangs (slow Mongo query, network flap, etc.).  Individual callers
// can still override per request by passing { timeout: N } explicitly.
axios.defaults.timeout = 30_000;

// Send httpOnly cookies on every request.  Required for Google OAuth users
// who never get a localStorage token — the backend issues a `Secure;
// SameSite=None; HttpOnly` cookie that we MUST send on cross-origin /api
// calls or they'll come back 401 and `user` stays null forever (which
// silently broke things like the Bingo auto-rep-select).
axios.defaults.withCredentials = true;

// Attach token from centralized store to every request
axios.interceptors.request.use((config) => {
  const token = TokenStore.getAccess();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Automatic token refresh on 401 responses.
//
// Bug history: previously each parallel-request subscriber attached only an
// `onSuccess` callback.  If the refresh itself failed (e.g. expired refresh
// token, network blip mid-refresh), the catch block cleared tokens + force-
// redirected to /login — but the queued subscribers were never notified.
// Their Promises hung forever, which meant the `.catch(() => ({data:[]}))`
// guards downstream never fired either.  Widgets stayed in loading state
// silently.  Regular users hit this every ~24h on the dashboard
// (multiple parallel /api/reps + /api/stores + /api/sheets + ... requests
// all 401 together).  Master/admin had 30-day tokens so they never hit it.
//
// Fix: subscribers now carry BOTH success and error callbacks.  Refresh
// failure rejects all queued subscribers cleanly so downstream .catch
// guards fire and widgets fall back gracefully.
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeToRefresh(onSuccess, onError) {
  refreshSubscribers.push({ onSuccess, onError });
}

function onRefreshed(token) {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  subs.forEach(s => s.onSuccess(token));
}

function failRefreshSubscribers(err) {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  subs.forEach(s => s.onError(err));
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const reqUrl = originalRequest?.url || "";
    // Endpoints whose 401s are expected during normal anonymous browsing —
    // the auth-check itself and the login/refresh endpoints.  Globally
    // redirecting on these would cause an infinite reload loop on the
    // login screen.
    // Game admin gates (bingo/conquest/ringer) end in "/admin/verify".
    // Match on suffix (not substring) so a legit session-expired call
    // to some other path that just happens to contain that string can't
    // slip past the logout redirect.
    const isGameAdminGate = /\/admin\/verify(\?.*)?$/.test(reqUrl);
    const isAuthProbe =
      reqUrl.includes("/api/auth/me") ||
      reqUrl.includes("/api/auth/login") ||
      reqUrl.includes("/api/auth/register") ||
      reqUrl.includes("/api/auth/refresh") ||
      isGameAdminGate;

    if (error.response?.status === 401 && !originalRequest._retry && TokenStore.getRefresh() && !isAuthProbe) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeToRefresh(
            (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            },
            (err) => reject(err),
          );
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = TokenStore.getRefresh();
        const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshToken });
        TokenStore.setTokens(data.access_token);
        isRefreshing = false;
        onRefreshed(data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        TokenStore.clear();
        // Reject all queued subscribers so their downstream .catch handlers
        // fire (otherwise they hang forever and the page silently stays in
        // loading state with empty dropdowns).
        failRefreshSubscribers(refreshError);
        // Only force-redirect if the user isn't already on the login page.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    if (error.response?.status === 401 && !isAuthProbe) {
      TokenStore.clear();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // We support BOTH localStorage Bearer tokens (email/password login) and
    // httpOnly session cookies (Google OAuth).  Don't bail out just because
    // localStorage is empty — the cookie may still authenticate us, and
    // skipping this call leaves `user` permanently null which silently
    // breaks every component that personalises based on `user.paired_rep`.
    //
    // Safety net: race the auth-check against a 12s hard timeout so iOS
    // Safari users never get stuck on an infinite spinner if the network
    // request stalls (Safari can pause in-flight fetches when the tab is
    // backgrounded or when the connection is flaky).  Worst case we show
    // them the login screen and they retry — far better than an indefinite
    // loading state with no way out.
    let safetyTimer;
    try {
      const reqPromise = axios.get(`${API}/api/auth/me`, { timeout: 12_000 });
      const timeoutPromise = new Promise((_, reject) => {
        safetyTimer = setTimeout(
          () => reject(new Error('auth_check_timeout')),
          12_000,
        );
      });
      const { data } = await Promise.race([reqPromise, timeoutPromise]);
      setUser(data);
    } catch (e) {
      const isTimeout = e?.message === 'auth_check_timeout' || e?.code === 'ECONNABORTED';
      if (isTimeout) {
        console.warn('[Auth] /api/auth/me timed out after 12s — user will see login screen');
      } else if (TokenStore.getAccess()) {
        console.warn('[Auth] Session check failed:', e.response?.status || e.message);
        TokenStore.clear();
      }
      setUser(null);
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip — AuthCallback handles session exchange
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      TokenStore.setTokens(data.access_token, data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name });
      TokenStore.setTokens(data.access_token, data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch (e) {
      console.warn('[Auth] Logout API call failed:', e.response?.status || e.message);
    }
    TokenStore.clear();
    // Invalidate window-level caches so the next user sees fresh data instantly
    // (prevents the mobile drawer from serving the previous user's hero / stale fields).
    try {
      delete window.__csDrawerSummary;
    } catch { /* noop */ }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { TokenStore };
